import { Card, CardContent, Input } from '@features/ui';
import { DuplicateIcon } from '@features/ui/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TAffiliateAccountProductLink } from '@src/types/affiliate';
import { toast } from 'react-toastify';
import { getAffiliateAccountLinks, joinAffiliateProgram } from '@src/services';
import { useGetMe } from '@src/hooks/useGetMe';

export const AffiliateAccountLinksPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accountLinks, setAccountLinks] = useState<
    TAffiliateAccountProductLink[]
  >([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const { me } = useGetMe();

  useEffect(() => {
    const fetchAffiliateLinks = async () => {
      try {
        setLoading(true);
        const response = await getAffiliateAccountLinks();

        setAccountLinks(response.data);
      } catch (error) {
        console.error('Failed to fetch affiliate links:', error);
        toast.error('Failed to load affiliate links. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliateLinks();
  }, []);

  const handleCopyLink = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy link to clipboard');
    }
  };

  const signUpForAffiliateProgram = async () => {
    try {
      await joinAffiliateProgram();
      toast.success('Successfully joined the affiliate program!');
    } catch (error) {
      console.error('Error joining affiliate program:', error);
      toast.error('Failed to join affiliate program. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl">
        <Card className="bg-background-tertiary border-none p-0">
          <CardContent className="px-0">
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <Card className="bg-background-tertiary border-none p-0">
        <CardContent className="px-0">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-4 text-sm">
            <button
              className="text-primary hover:underline bg-transparent border-none cursor-pointer p-0"
              onClick={() => navigate('/affiliates/banner-and-links')}
            >
              Affiliate info
            </button>
            <span className="text-muted-foreground">&gt;</span>
            <span className="text-muted-foreground">
              Affiliate Account Links
            </span>
          </div>

          {/* Header */}
          <h1 className="text-2xl font-bold mb-6">Affiliate info</h1>

          {/* Registration Info */}
          <div className="mb-6 space-y-4">
            <p className="text-sm">
              To register to become a Leeloo Affiliate,{' '}
              <button
                className="text-primary hover:underline font-semibold bg-transparent border-none cursor-pointer p-0"
                onClick={signUpForAffiliateProgram}
              >
                CLICK HERE
              </button>{' '}
              <span className="italic">
                (if you are redirected to this page after clicking the link, you
                are already signed up as an affiliate - Thank you!)
              </span>
            </p>

            <p className="text-sm">
              The Leeloo Affiliate Program is our way of saying thank you for
              believing in us and for helping spread the word about Leeloo. It
              is also your way of helping fellow traders save money and show
              appreciation to our partners.
            </p>

            <p className="text-sm">
              As a Leeloo Affiliate, you are paid 10% for the six-month lifespan
              of each trader you send to Leeloo. It is all tracked for you with
              your Affiliate link or coupon code.
            </p>

            <p className="text-sm">
              We look forward to having you on our team!
            </p>
          </div>

          {/* Terms and Conditions */}
          <div className="bg-gray-700 p-4 rounded mb-6">
            <p className="text-lg">
              Please read our{' '}
              <button
                className="text-primary hover:underline bg-transparent border-none cursor-pointer p-0"
                onClick={() => {
                  /* Add terms link handler */
                }}
              >
                terms and conditions
              </button>{' '}
              before signing up.
            </p>
          </div>

          {/* General Affiliate Link Section */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">
              Your General Affiliate Link
            </h2>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  readOnly
                  className="bg-background-secondary border-border pr-10"
                  value={me?.affiliate_landing_link || ''}
                />
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer"
                  title="Copy link"
                  onClick={() =>
                    handleCopyLink(me?.affiliate_landing_link || '', -1)
                  }
                >
                  <DuplicateIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            {copiedIndex === -1 && (
              <p className="text-sm text-primary mt-2">
                Link copied to clipboard!
              </p>
            )}
          </div>

          {/* Banners and Links Section */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Banners and Links</h2>

            <div className="space-y-6">
              {accountLinks.map((link, index) => (
                <div
                  key={link.id}
                  className="border border-border rounded-lg p-4 bg-background-secondary"
                >
                  {/* Title */}
                  <div className="mb-3">
                    <h3 className="font-semibold text-base mb-1">Title:</h3>
                    <button
                      className="text-primary hover:underline bg-transparent border-none cursor-pointer p-0 text-left"
                      onClick={() =>
                        window.location.replace(
                          link.affiliate_links.checkout_link
                        )
                      }
                    >
                      {link.title}
                    </button>
                  </div>

                  {/* Affiliate Link Generated */}
                  <div className="mb-3">
                    <h3 className="font-semibold text-base mb-1">
                      Affiliate Link Generated:
                    </h3>
                    <div className="flex gap-2 items-center">
                      <button
                        className="text-primary hover:underline text-sm bg-transparent border-none cursor-pointer p-0 break-all text-left flex-1"
                        onClick={() =>
                          window.location.replace(
                            link.affiliate_links.checkout_link
                          )
                        }
                      >
                        {link.affiliate_links.checkout_link}
                      </button>
                      <button
                        className="text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer p-1 flex-shrink-0"
                        title={copiedIndex === index ? 'Copied!' : 'Copy link'}
                        onClick={() =>
                          handleCopyLink(
                            link.affiliate_links.checkout_link,
                            index
                          )
                        }
                      >
                        <DuplicateIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <h3 className="font-semibold text-base mb-1">
                      Description:
                    </h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {link.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
